// src/pages/settings/PaymentSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import Header from "@/components/ui/Header";
import Sidebar from "@/components/ui/Sidebar";
import CancelSubscriptionModal from "@/components/billing/CancelSubscriptionModal";
import CancelledBanner from "@/components/billing/CancelledBanner";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  CreditCard,
  Landmark,
  RefreshCw,
  FileText,
} from "lucide-react";

export default function PaymentSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBusinessId, refreshMe } = useAuth();
  const CYCLE_LABELS = {
    monthly: t("billing.cycleMonthly"),
    yearly: t("billing.cycleYearly"),
    lifetime: t("billing.cycleLifetime"),
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);

  // Subscription cancel state
  const [catalogBiz, setCatalogBiz] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Oblio invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const loadStatus = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/billing/connect/status", {
        headers: { "X-Business-Id": currentBusinessId },
      });
      setStatus(data);
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "Nu am putut încărca status-ul.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();

    // Check for return from Stripe
    const qs = new URLSearchParams(location.search || "");
    if (qs.get("stripe_return") === "1" || qs.get("stripe_refresh") === "1") {
      // Refresh status after Stripe onboarding
      setTimeout(() => {
        loadStatus();
      }, 1000);

      // Clean URL
      qs.delete("stripe_return");
      qs.delete("stripe_refresh");
      const newSearch = qs.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, {
        replace: true,
      });
    }
  }, [currentBusinessId, location.pathname]);

  const handleDisconnect = async () => {
    if (
      !confirm(
        "Sigur vrei să deconectezi Stripe Connect? Nu vei mai putea primi plăți pentru programări."
      )
    ) {
      return;
    }

    setDisconnecting(true);
    setError("");
    try {
      await api.post(
        "/billing/connect/disconnect",
        {},
        {
          headers: { "X-Business-Id": currentBusinessId },
        }
      );
      await loadStatus();
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Eroare la deconectare.";
      setError(detail);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  // Load subscription info from billing catalog
  const loadSubscription = async () => {
    setSubLoading(true);
    try {
      const { data } = await api.get("/billing/catalog");
      if (data?.business) setCatalogBiz(data.business);
    } catch (_) {
      // non-critical: subscription section just won't show
    } finally {
      setSubLoading(false);
    }
  };

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const { data } = await api.get("/billing/invoices", {
        headers: { "X-Business-Id": currentBusinessId },
      });
      setInvoices(Array.isArray(data) ? data : []);
    } catch (_) {
      // non-critical
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
    loadInvoices();
  }, [currentBusinessId]);

  const hasActiveSub =
    catalogBiz &&
    ["active", "trialing"].includes(catalogBiz?.subscription_status);

  const cancelAtPeriodEnd = !!catalogBiz?.cancel_at_period_end;
  const isLifetime = catalogBiz?.billing_cycle === "lifetime";
  const hasStripeSub = !!catalogBiz?.stripe_subscription_id && !isLifetime;

  const reloadSubscription = async () => {
    try {
      const { data } = await api.get("/billing/catalog");
      if (data?.business) setCatalogBiz(data.business);
      await refreshMe({ silent: true });
    } catch (_) {}
  };

  const onCancelConfirm = async ({ reason, comment }) => {
    setCancelling(true);
    try {
      await api.post("/billing/cancel", { reason, comment });
      setShowCancelModal(false);
      await reloadSubscription();
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.stripe?.[0] ||
        e?.response?.data?.subscription?.[0] ||
        e?.message ||
        "Nu am putut anula abonamentul.";
      setError(detail);
    } finally {
      setCancelling(false);
    }
  };

  const StatusBadge = ({ connected, chargesEnabled, payoutsEnabled }) => {
    if (!connected) {
      return (
        <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
          <XCircle size={14} />
          <span className="font-medium">Neconectat</span>
        </div>
      );
    }

    if (chargesEnabled && payoutsEnabled) {
      return (
        <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
          <CheckCircle size={14} />
          <span className="font-medium">Activ</span>
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
        <AlertTriangle size={14} />
        <span className="font-medium">În așteptare</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        isCollapsed={sidebarCollapsed}
        onToggleSidebar={handleSidebarToggle}
      />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="max-w-3xl mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-xl font-semibold mb-4">Setări Plăți</h1>
          </div>

          {error && (
            <div className="mb-3 text-sm px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl border p-4 text-muted-foreground">
              Se încarcă...
            </div>
          ) : (
            <>
              {/* Subscription Section */}
              {!subLoading && hasActiveSub ? (
                <div className="rounded-2xl border p-4 mb-6">
                  <h2 className="font-medium mb-2">Abonament</h2>

                  <div className="rounded border border-border bg-muted p-4 text-foreground">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">Plan</div>
                        <div className="font-medium">
                          {catalogBiz?.plan || catalogBiz?.plan_code || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Perioadă</div>
                        <div className="font-medium">
                          {CYCLE_LABELS[catalogBiz?.billing_cycle] || catalogBiz?.billing_cycle || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Expiră la</div>
                        <div className="font-medium">
                          {catalogBiz?.current_period_end
                            ? new Date(catalogBiz.current_period_end).toLocaleDateString("ro-RO", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </div>
                      </div>
                    </div>

                    {cancelAtPeriodEnd ? (
                      <div className="mt-4">
                        <CancelledBanner
                          periodEnd={catalogBiz?.current_period_end}
                          onUndone={reloadSubscription}
                        />
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="mt-4 text-sm px-3 py-2 rounded border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        Anulează abonamentul
                      </button>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Facturi Oblio */}
              {!invoicesLoading && invoices.length > 0 ? (
                <div className="rounded-2xl border p-4 mb-6">
                  <h2 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="text-muted-foreground" size={18} />
                    Facturi
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="pb-2 pr-4 font-medium">Data</th>
                          <th className="pb-2 pr-4 font-medium">Serie / Nr</th>
                          <th className="pb-2 pr-4 font-medium">Sumă</th>
                          <th className="pb-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id} className="border-b last:border-0">
                            <td className="py-2 pr-4">
                              {new Date(inv.created_at).toLocaleDateString("ro-RO", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </td>
                            <td className="py-2 pr-4 font-mono">
                              {inv.oblio_series}{inv.oblio_number || "—"}
                            </td>
                            <td className="py-2 pr-4">
                              {inv.amount} {inv.currency}
                            </td>
                            <td className="py-2">
                              {inv.status === "issued" ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                  <CheckCircle size={12} /> Emisă
                                </span>
                              ) : inv.status === "cancelled" ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                  <XCircle size={12} /> Anulată
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                  title={inv.error_message || ""}
                                >
                                  <AlertTriangle size={12} /> Eroare
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Stripe Connect Status */}
              <div className="rounded-2xl border p-4 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="font-medium mb-1 flex items-center gap-2">
                      <CreditCard className="text-muted-foreground" />
                      Stripe Connect
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Primește plăți direct în contul tău bancar
                    </p>
                  </div>
                  <StatusBadge
                    connected={status?.connected}
                    chargesEnabled={status?.charges_enabled}
                    payoutsEnabled={status?.payouts_enabled}
                  />
                </div>

                {!status?.connected ? (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-lg bg-muted border border-border p-4 space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Stripe Connect îți permite să primești plăți cu cardul de la clienți direct în contul tău bancar — fără ca banii să treacă prin AppointIQ.
                      </p>

                      <ol className="space-y-3">
                        {[
                          { n: "1", title: "Creezi un cont Stripe gratuit", desc: "Apasă butonul de mai jos. Stripe îți ghidează procesul în ~3 minute." },
                          { n: "2", title: "Completezi datele firmei", desc: "Nume, CUI/CNP, IBAN cont bancar. Stripe verifică identitatea conform legii (KYC)." },
                          { n: "3", title: "Activezi plata pe servicii", desc: "Mergi la Servicii, editeaza un serviciu si bifeaza 'Necesita plata avans'." },
                          { n: "4", title: "Clienții plătesc online", desc: "Când se programează, sunt redirecționați la Stripe Checkout. Banii ajung direct la tine." },
                        ].map(({ n, title, desc }) => (
                          <li key={n} className="flex gap-3">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{n}</span>
                            <div>
                              <div className="text-sm font-medium">{title}</div>
                              <div className="text-xs text-muted-foreground">{desc}</div>
                            </div>
                          </li>
                        ))}
                      </ol>

                      <div className="pt-2 border-t border-border text-xs text-muted-foreground">
                        <strong>Comision AppointIQ:</strong> 5% + 1 RON per tranzacție (reținut automat). Restul ajunge direct în contul tău.
                      </div>
                    </div>

                    <button
                      onClick={() => navigate("/app/settings/payments/connect")}
                      className="w-full sm:w-auto px-4 py-2 rounded bg-cyan-800 text-white font-medium hover:bg-teal-700 dark:bg-cyan-700 dark:hover:bg-teal-800"
                    >
                      Activează Stripe Connect
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 space-y-4">
                    {/* Connected - Show details */}
                    <div className="rounded border border-border bg-muted p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">
                            Account ID
                          </div>
                          <div className="font-mono text-sm">
                            {status?.account_id}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Status</div>
                          <div className="text-sm font-medium capitalize">
                            {status?.status}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                            <CreditCard className="text-green-600" />
                            Plăți Card
                          </div>
                          <div
                            className={`text-sm font-medium ${
                              status?.charges_enabled
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            {status?.charges_enabled ? "Activate" : "Dezactivate"}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                            <Landmark className="text-blue-600" />
                            Payout-uri
                          </div>
                          <div
                            className={`text-sm font-medium ${
                              status?.payouts_enabled
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            {status?.payouts_enabled ? "Activate" : "Dezactivate"}
                          </div>
                        </div>

                        {status?.country && (
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">Țară</div>
                            <div className="text-sm font-medium">
                              {status.country.toUpperCase()}
                            </div>
                          </div>
                        )}

                        {status?.default_currency && (
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">
                              Monedă
                            </div>
                            <div className="text-sm font-medium">
                              {status.default_currency}
                            </div>
                          </div>
                        )}
                      </div>

                      {status?.requirements &&
                        Object.keys(status.requirements).length > 0 && (
                          <div className="mt-4 p-3 rounded bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
                            <div className="text-sm font-medium text-yellow-800 mb-1">
                              ⚠️ Cerințe Stripe
                            </div>
                            <div className="text-xs text-yellow-700">
                              Contul tău necesită informații suplimentare. Vei fi
                              contactat de Stripe.
                            </div>
                          </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={loadStatus}
                        className="px-3 py-2 rounded border text-sm hover:bg-muted flex items-center gap-2"
                      >
                        <RefreshCw />
                        Actualizează Status
                      </button>

                      <button
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        className="px-3 py-2 rounded border border-red-300 text-red-600 text-sm hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                      >
                        {disconnecting ? "Se deconectează..." : "Deconectează"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Configuration (TODO: future) */}
              {status?.connected && status?.charges_enabled && (
                <div className="rounded-2xl border p-4 bg-muted">
                  <h2 className="font-medium mb-2">
                    Configurare Plăți
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configurează avansul și politica de refund pentru serviciile tale.
                  </p>
                  <button
                    onClick={() => {
                      alert(
                        "Configurare avans și refund policy - coming soon în Services!"
                      );
                    }}
                    className="px-3 py-2 rounded border border-border hover:bg-background text-sm"
                  >
                    Configurează (Coming Soon)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <CancelSubscriptionModal
          open={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={onCancelConfirm}
          periodEnd={catalogBiz?.current_period_end}
          immediate={!hasStripeSub}
          loading={cancelling}
        />
      </main>
    </div>
  );
}
